/**
 * Image Generation API Service
 *
 * Provides methods to interact with the Fooocus-based image generation backend.
 * Supports text-to-image generation, style selection, and job status polling.
 */

import type {
  ImageGenerationConfig,
  ImageGenerationResponse,
  ImageJobStatus,
  StyleInfo,
  ResolutionInfo,
  ApiResponse,
  UpscaleVaryRequest,
  InpaintRequest,
  ImagePromptRequest,
  DescribeRequest,
  DescribeResponse,
  RemoveBackgroundRequest,
  RemoveBackgroundResponse,
  ImageEnhanceRequest,
  ImageModelInfo,
} from '../types';
import {
  DEFAULT_AI_IMAGE_PROVIDER,
  DESKTOP_DISPATCHABLE_IMAGE_PROVIDERS,
} from '../constants/imageSource';
import type { AIImageProvider } from '../constants/imageSource';
import { buildCloudApiUrl, getApiBaseUrl } from './urlResolver';
import type { UnifiedJob } from './jobs';
import { buildImagePricingFields } from './imageGenerationParams';
import {
  requestWrapped,
  extractErrorMessage,
  extractApiErrorMessage,
  getAuthHeadersFromStorage,
} from '../utils/httpUtils';

function isCloudOnlyImageProvider(provider: string | undefined): boolean {
  if (!provider) return false;
  return !DESKTOP_DISPATCHABLE_IMAGE_PROVIDERS.has(provider as AIImageProvider);
}

function getImageGenerationBaseUrl(provider: string | undefined): string {
  if (isCloudOnlyImageProvider(provider)) {
    return buildCloudApiUrl('');
  }
  return getApiBaseUrl();
}

function buildImageGenerationUrl(provider: string | undefined, endpoint: string): string {
  const baseUrl = getImageGenerationBaseUrl(provider).replace(/\/$/, '');
  const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalized}`;
}

const jobProviderRegistry = new Map<string, string>();

export function registerJobProvider(jobId: string, provider: string | undefined): void {
  if (jobId && provider) {
    jobProviderRegistry.set(jobId, provider);
  }
}

function lookupJobProvider(jobId: string): string | undefined {
  return jobProviderRegistry.get(jobId);
}

/**
 * Get the display URL for an image. Returns the URL as-is since <img> tags
 * can load cross-origin URLs without CORS restrictions.
 */
export function getProxiedImageUrl(url: string): string {
  return url;
}

/**
 * Get the download URL for an image via server-side proxy.
 * Uses /api/v1/image-editing/download which bypasses CORS for all
 * supported CDN domains (RunPod, fal.ai, CloudFront, S3, GCS).
 *
 * For local URLs (data:, blob:), returns them directly.
 */
export function getImageDownloadUrl(url: string): string {
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  const baseUrl = getApiBaseUrl();
  const encodedUrl = encodeURIComponent(url);
  return `${baseUrl}/api/v1/image-editing/download?url=${encodedUrl}`;
}

/**
 * Image Generation API Service
 */
export const imageGenerationAPI = {
  /**
   * Get available models for a provider
   * @param provider The provider ID (default: fooocus)
   * @returns List of models for the provider
   */
  async getModels(provider: string = DEFAULT_AI_IMAGE_PROVIDER): Promise<
    ApiResponse<{
      provider: string;
      models: ImageModelInfo[];
      total: number;
      default: string | null;
      supports_models: boolean;
    }>
  > {
    const baseUrl = getApiBaseUrl();
    const url = new URL(`${baseUrl}/api/v1/image/models`);
    url.searchParams.set('provider', provider);
    return requestWrapped(
      url.toString(),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get models'
    );
  },

  /**
   * Search CivitAI for SDXL checkpoint models
   * @param query Search query string (empty = popular models)
   * @param limit Number of results per page
   * @returns Search results with model metadata
   */
  async searchModels(
    query: string = '',
    limit: number = 20
  ): Promise<
    ApiResponse<{
      models: ImageModelInfo[];
      total: number;
    }>
  > {
    const baseUrl = getApiBaseUrl();
    const url = new URL(`${baseUrl}/api/v1/image/models/search`);
    if (query) url.searchParams.set('query', query);
    url.searchParams.set('limit', String(limit));
    return requestWrapped(
      url.toString(),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to search models'
    );
  },

  /**
   * Install a checkpoint into a local image provider's folder (Fooocus SDXL /
   * ComfyUI SD 1.5). Identify the model by curated slug (ComfyUI SD) or a
   * CivitAI model/version id. Returns a job_id to poll via
   * getImageModelInstallStatus.
   */
  async installImageModel(params: {
    provider: string;
    slug?: string | null;
    civitai_version_id?: number | null;
    civitai_model_id?: number | null;
    hf_repo?: string | null;
    hf_filename?: string | null;
  }): Promise<ApiResponse<{ job_id: string }>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/components/image-models/install`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeadersFromStorage()) },
        body: JSON.stringify(params),
      },
      'Failed to install model'
    );
  },

  /**
   * Poll the status of an image-model install job.
   */
  async getImageModelInstallStatus(jobId: string): Promise<
    ApiResponse<{
      status: 'queued' | 'downloading' | 'verifying' | 'installed' | 'failed';
      progress_mb: number;
      total_mb: number;
      error: string | null;
    }>
  > {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/components/image-models/install/${encodeURIComponent(jobId)}/status`,
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get status'
    );
  },

  /**
   * Delete an installed checkpoint from a local image provider's folder.
   */
  async deleteImageModel(
    provider: string,
    filename: string
  ): Promise<ApiResponse<{ ok: boolean; removed: boolean }>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/components/image-models/${encodeURIComponent(
        provider
      )}/${encodeURIComponent(filename)}`,
      { method: 'DELETE', headers: await getAuthHeadersFromStorage() },
      'Failed to delete model'
    );
  },

  /**
   * Generate images from text prompt
   * @param config Image generation configuration
   * @returns Generation response with job ID or images (sync mode)
   */
  async generateImage(
    config: Partial<ImageGenerationConfig>
  ): Promise<ApiResponse<ImageGenerationResponse>> {
    const provider = config.provider || DEFAULT_AI_IMAGE_PROVIDER;
    const url = buildImageGenerationUrl(provider, '/api/v1/image/generate');

    const result = await requestWrapped<ImageGenerationResponse>(
      url,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          prompt: config.prompt || '',
          negative_prompt: config.negative_prompt || '',
          styles: config.styles || [],
          seed: config.seed ?? -1,
          guidance_scale: config.guidance_scale || 4.0,
          sharpness: config.sharpness || 2.0,
          performance: config.performance || 'Speed',
          output_format: config.output_format || 'png',
          async_process: config.async_process ?? false,
          // resolution / width / height / num_images / num_steps / provider /
          // model come from the ONE builder the cost card also uses, so the
          // price quoted beside this button is computed from the same fields
          // the button sends. `num_steps: config.num_steps || 30` used to live
          // here: it turned the 0 that the six OpenRouter providers carry
          // (steps do not apply to them) into a 30 the user never chose, while
          // the card sent the 0 and was refused.
          ...buildImagePricingFields({ ...config, provider }),
        }),
      },
      'Failed to generate image'
    );

    if (result.success && result.data?.job_id) {
      registerJobProvider(result.data.job_id, provider);
    }

    return result;
  },

  /**
   * Get status of an async image generation job.
   *
   * ONE polling endpoint (integrationMode Stage 01). The per-capability
   * `/api/v1/image/job/{id}` route this used to call is deleted: the
   * "image_generation" store is registered with the unified job registry on
   * BOTH servers, so `/api/v1/jobs/{id}` has always resolved these jobs — and
   * unlike the duplicate, it is in GRANT_INFRASTRUCTURE_OPERATIONS, so an
   * application grant holding `image:generate` can watch the job it started.
   *
   * This is the ONE place the unified shape is converted to `ImageJobStatus`
   * (the store's own payload travels under `extra_metadata.result`), so no
   * caller — including `imagePollAdapter` — ever sees two response shapes.
   *
   * The URL still goes through `buildImageGenerationUrl`: a cloud-only
   * provider's job was submitted straight to Cloud Run, so that is where its
   * record lives and where the poll has to go.
   *
   * @param jobId The job ID returned from generateImage
   * @returns Job status with progress and result when completed
   */
  async getJobStatus(jobId: string): Promise<ApiResponse<ImageJobStatus>> {
    const provider = lookupJobProvider(jobId);
    const response = await requestWrapped<UnifiedJob>(
      buildImageGenerationUrl(provider, `/api/v1/jobs/${jobId}`),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get job status'
    );
    if (!response.success || !response.data) {
      return response as unknown as ApiResponse<ImageJobStatus>;
    }

    const job = response.data;
    const extra = (job.extra_metadata ?? {}) as Record<string, unknown>;
    return {
      ...response,
      data: {
        job_id: job.job_id,
        status: job.status as ImageJobStatus['status'],
        progress: job.progress,
        message: job.message,
        error: job.error ?? undefined,
        result: (extra.result as ImageJobStatus['result'] | null) ?? undefined,
      },
    } as ApiResponse<ImageJobStatus>;
  },

  /**
   * Get available styles for image generation
   * @param category Optional category to filter styles
   * @returns List of available styles
   */
  async getStyles(
    category?: string
  ): Promise<ApiResponse<{ styles: StyleInfo[]; total: number; categories: string[] }>> {
    const baseUrl = getApiBaseUrl();
    const url = new URL(`${baseUrl}/api/v1/image/styles`);
    if (category) {
      url.searchParams.set('category', category);
    }
    return requestWrapped(
      url.toString(),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get styles'
    );
  },

  /**
   * Get available resolution presets
   * @param provider Optional provider ID to filter resolutions
   * @returns List of available resolutions
   */
  async getResolutions(provider?: string): Promise<
    ApiResponse<{
      resolutions: ResolutionInfo[];
      total: number;
      categories: string[];
      default: string;
      provider?: string;
      pricing_model?: string;
    }>
  > {
    const baseUrl = getApiBaseUrl();
    const url = new URL(`${baseUrl}/api/v1/image/resolutions`);
    if (provider) {
      url.searchParams.set('provider', provider);
    }
    return requestWrapped(
      url.toString(),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get resolutions'
    );
  },

  /**
   * Check if the image generation service is healthy
   * @returns Health status
   */
  async checkHealth(): Promise<
    ApiResponse<{ status: string; fooocus_available: boolean; mode?: string }>
  > {
    const baseUrl = getApiBaseUrl();

    try {
      const response = await fetch(`${baseUrl}/api/v1/image/health`, {
        method: 'GET',
        headers: await getAuthHeadersFromStorage(),
      });

      const data = await response.json();

      // Health check intentionally returns the body even on failure so callers
      // can inspect partial status — it does NOT use requestWrapped() (which
      // drops data on a non-OK response).
      return {
        success: response.ok,
        data: data.data || data,
        error: !response.ok ? extractApiErrorMessage(data, 'Health check failed') : undefined,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Upscale or create variations of an image
   */
  async upscaleVaryImage(
    request: UpscaleVaryRequest
  ): Promise<ApiResponse<ImageGenerationResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/upscale`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          image: request.image,
          method: request.method,
          prompt: request.prompt || '',
          negative_prompt: request.negative_prompt || '',
          styles: request.styles || [],
          seed: request.seed ?? -1,
          async_process: request.async_process ?? false,
          model: request.model,
        }),
      },
      'Failed to process image'
    );
  },

  /**
   * Inpaint or outpaint an image
   */
  async inpaintImage(request: InpaintRequest): Promise<ApiResponse<ImageGenerationResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/inpaint`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          image: request.image,
          mask: request.mask || '',
          prompt: request.prompt || '',
          negative_prompt: request.negative_prompt || '',
          styles: request.styles || [],
          inpaint_method: request.inpaint_method || 'Inpaint or Outpaint (default)',
          outpaint_directions: request.outpaint_directions || [],
          outpaint_distance_left: request.outpaint_distance_left || 0,
          outpaint_distance_right: request.outpaint_distance_right || 0,
          outpaint_distance_top: request.outpaint_distance_top || 0,
          outpaint_distance_bottom: request.outpaint_distance_bottom || 0,
          seed: request.seed ?? -1,
          async_process: request.async_process ?? false,
          model: request.model,
        }),
      },
      'Failed to inpaint image'
    );
  },

  /**
   * Generate images using reference images (ControlNet, FaceSwap)
   */
  async imagePromptGenerate(
    request: ImagePromptRequest
  ): Promise<ApiResponse<ImageGenerationResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/prompt`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          prompt: request.prompt || '',
          negative_prompt: request.negative_prompt || '',
          images: request.images.map((img) => ({
            image: img.image,
            weight: img.weight ?? 1.0,
            stop: img.stop ?? 1.0,
            control_type: img.control_type || 'ImagePrompt',
          })),
          styles: request.styles || [],
          resolution: request.resolution || '1152x896',
          seed: request.seed ?? -1,
          guidance_scale: request.guidance_scale ?? 4.0,
          num_steps: request.num_steps ?? 30,
          async_process: request.async_process ?? false,
          model: request.model,
        }),
      },
      'Failed to generate with image prompt'
    );
  },

  /**
   * Describe an image and generate a prompt from it
   */
  async describeImage(request: DescribeRequest): Promise<ApiResponse<DescribeResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/describe`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          image: request.image,
          describe_type: request.describe_type || 'Photograph',
        }),
      },
      'Failed to describe image'
    );
  },

  /**
   * Remove background from an image
   * Returns a PNG with transparent background
   */
  async removeBackground(
    request: RemoveBackgroundRequest
  ): Promise<ApiResponse<RemoveBackgroundResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/remove-background`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          image: request.image,
          mask_model: request.mask_model || 'u2net',
          output_format: request.output_format || 'png',
        }),
      },
      'Failed to remove background'
    );
  },

  /**
   * Enhance image quality with optional region-specific improvements
   */
  async enhanceImage(request: ImageEnhanceRequest): Promise<ApiResponse<ImageGenerationResponse>> {
    const baseUrl = getApiBaseUrl();
    return requestWrapped(
      `${baseUrl}/api/v1/image/enhance`,
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          image: request.image,
          prompt: request.prompt || '',
          negative_prompt: request.negative_prompt || '',
          enhance_checkbox: request.enhance_checkbox ?? true,
          uov_method: request.uov_method || 'Vary (Strong)',
          enhance_regions: request.enhance_regions || [],
          seed: request.seed ?? -1,
          async_process: request.async_process ?? false,
          model: request.model,
        }),
      },
      'Failed to enhance image'
    );
  },
};

export default imageGenerationAPI;
